export type AttendanceStatus = 'not_arrived' | 'checked_in' | 'picked_up' | 'absent';
export type CheckedInBy = 'parent' | 'teacher' | 'admin';
/** Who wrote the check-out. `parent` is a self check-out from the parent pickup screen. */
export type CheckedOutBy = 'parent' | 'teacher' | 'admin';

export interface AttendanceRecord {
  id: string;
  studentId: string;
  programId: string;
  enrollmentId: string;
  sessionDate: string; // YYYY-MM-DD
  status: AttendanceStatus;
  checkedInAt: string | null;
  checkedInBy: CheckedInBy | null;
  checkedInByUserId: string | null;
  /** Display name of whoever recorded the check-in, captured at the time of the action. */
  checkedInByName: string | null;
  pickedUpAt: string | null;
  pickedUpByName: string | null;
  pickupIsAuthorized: boolean | null;
  pickupRecordedByUserId: string | null;
  /** Display name of the staff member who released the student, at the time of the action. */
  pickupRecordedByName: string | null;
  /**
   * Who recorded the check-out. Null on open rows and on every row closed before the column
   * existed (2026-10-16), so null means "staff, probably" — never "nobody".
   */
  checkedOutBy: CheckedOutBy | null;
  parentDismissedAt: string | null;
  /** Free text — the UI offers presets but allows anything. Not an enum. */
  absenceReason: string | null;
  /**
   * `programs.type` for `programId`, which decides whether this record is a pickup-line
   * handover or a class simply ending (see `attendanceMode`).
   *
   * Only the parent-facing query embeds it, because only the parent surfaces have to make
   * that distinction from the record alone — a teacher is already looking at one program's
   * roster. It is `null` everywhere else, which `attendanceModeFor` reads as "unknown".
   */
  programType: string | null;
  /**
   * `''` for a real session of `programId`. Otherwise a virtual class such as
   * `'chinese:3'`, where `programId` and `enrollmentId` point at the child's *after-school*
   * seat — the thing that actually puts them in the class.
   *
   * Which means `programType` on a virtual record reads `'after-school'`, and
   * `attendanceModeFor` would call it a pickup. Any surface that asks "was this a
   * handover?" must therefore check this field first; the parent surfaces are protected in
   * RLS rather than relying on it.
   */
  virtualClassKey: string;
  createdAt: string;
  updatedAt: string;
}

/** The discriminator value for a real (non-virtual) session. */
export const REAL_SESSION_KEY = '';

export interface AuthorizedPickup {
  id: string;
  studentId: string;
  name: string;
  relationship: string | null;
  phone: string | null;
  /**
   * Object path in the private `student-photos` bucket, or null when no photo is on file.
   * A path, never a URL — reading it needs a freshly signed URL, same as a student headshot.
   */
  photoPath: string | null;
  createdAt: string;
}

// Row shapes matching the DB column names (snake_case), used for raw Supabase queries
export interface AttendanceRecordRow {
  id: string;
  student_id: string;
  program_id: string;
  enrollment_id: string;
  session_date: string;
  status: AttendanceStatus;
  checked_in_at: string | null;
  checked_in_by: CheckedInBy | null;
  checked_in_by_user_id: string | null;
  checked_in_by_name: string | null;
  picked_up_at: string | null;
  picked_up_by_name: string | null;
  pickup_is_authorized: boolean | null;
  pickup_recorded_by_user_id: string | null;
  pickup_recorded_by_name: string | null;
  checked_out_by: CheckedOutBy | null;
  parent_dismissed_at: string | null;
  absence_reason: string | null;
  virtual_class_key: string;
  /** Embedded by the parent-facing query only; absent from a plain `select('*')`. */
  programs?: { type: string } | null;
  created_at: string;
  updated_at: string;
}

export interface AuthorizedPickupRow {
  id: string;
  student_id: string;
  name: string;
  relationship: string | null;
  phone: string | null;
  photo_path: string | null;
  created_at: string;
}

export function toAttendanceRecord(row: AttendanceRecordRow): AttendanceRecord {
  return {
    id: row.id,
    studentId: row.student_id,
    programId: row.program_id,
    enrollmentId: row.enrollment_id,
    sessionDate: row.session_date,
    status: row.status,
    checkedInAt: row.checked_in_at,
    checkedInBy: row.checked_in_by,
    checkedInByUserId: row.checked_in_by_user_id,
    checkedInByName: row.checked_in_by_name,
    pickedUpAt: row.picked_up_at,
    pickedUpByName: row.picked_up_by_name,
    pickupIsAuthorized: row.pickup_is_authorized,
    pickupRecordedByUserId: row.pickup_recorded_by_user_id,
    pickupRecordedByName: row.pickup_recorded_by_name,
    // Missing on a database that has not taken the patch yet, which is the same as "not
    // recorded" — every such row was closed by staff, if at all.
    checkedOutBy: row.checked_out_by ?? null,
    parentDismissedAt: row.parent_dismissed_at,
    absenceReason: row.absence_reason,
    programType: row.programs?.type ?? null,
    // Older callers that select an explicit column list may not ask for it; treating a
    // missing value as "real session" matches every row written before virtual classes.
    virtualClassKey: row.virtual_class_key ?? REAL_SESSION_KEY,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function toAuthorizedPickup(row: AuthorizedPickupRow): AuthorizedPickup {
  return {
    id: row.id,
    studentId: row.student_id,
    name: row.name,
    relationship: row.relationship,
    phone: row.phone,
    // Older callers that select an explicit column list may not ask for it, and a database
    // that has not taken the patch yet simply has no column — either way, "no photo".
    photoPath: row.photo_path ?? null,
    createdAt: row.created_at,
  };
}
